import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from '@angular/core';
import { IonIcon, IonItem, IonLabel, IonSpinner, ModalController } from '@ionic/angular/standalone';

import { ProFeature, ProMessage } from '../../../../../common/enums/pro-feature-message.enum';
import { ProGateService } from '../../../../core/payment/services/pro-gate.service';
import { SyncService } from '../../sync/sync.service';
import { SyncSheetComponent } from './sync-sheet.component';

/**
 * Settings → Account "Cloud sync" row. Surfaces the current SyncService state
 * with no controls of its own — the controls live in the sheet that opens on
 * tap (SyncSheetComponent), keeping the main Settings page calm.
 *
 * Manual backup (no auto-sync). Rendering mirrors SyncService.status:
 *   disabled — "Cloud backup — Pro feature" (tap → upsell)
 *   idle     — "Cloud backup · Backed up 2 min ago" + cloud-done icon
 *   syncing  — spinner + "Backing up 12 of 34…"
 *   offline  — "Offline — connect to back up" + cloud-offline icon
 *   error    — red tint + last error message
 */
@Component({
  selector: 'app-sync-row',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonIcon, IonItem, IonLabel, IonSpinner],
  template: `
    <ion-item
      class="sync-row"
      [class.sync-row--error]="status() === 'error'"
      button
      [detail]="status() !== 'syncing'"
      (click)="openSheet()"
    >
      <ion-icon [name]="iconName()" slot="start" [color]="iconColor()" aria-hidden="true"></ion-icon>
      <ion-label>
        <div class="sync-row__title">{{ title() }}</div>
        <p class="muted sync-row__sub">{{ subtitle() }}</p>
      </ion-label>
      @if (status() === 'syncing') {
        <ion-spinner slot="end" name="crescent"></ion-spinner>
      }
    </ion-item>
  `,
  styles: [`
    /* This row lives inside Settings' grouped .settings-group card, so it draws
       no card of its own — just a transparent, aligned ion-item. The parent's
       .settings-group rule can't reach in through view encapsulation, so the
       padding/height that match the sibling rows are set here directly. */
    :host { display: block; }
    ion-item {
      --background: transparent;
      --border-width: 0;
      --padding-start: 16px;
      --inner-padding-end: 12px;
      --min-height: 54px;
    }
    .sync-row__title { font-weight: 600; letter-spacing: -0.01em; }
    .sync-row__sub { margin: 2px 0 0; }
    .sync-row--error { --background: rgba(239, 68, 68, 0.06); }
  `],
})
export class SyncRowComponent {
  private readonly sync = inject(SyncService);
  private readonly modalCtrl = inject(ModalController);
  private readonly proGate = inject(ProGateService);

  /** Fires when the cloud-backup sheet is dismissed, so the host (Settings) can
   *  re-pull server-derived state (e.g. cloud storage usage) the backup changed.
   *  A modal close does NOT trigger the page's ionViewWillEnter, so without this
   *  the storage figure would stay stale until the user leaves and returns. */
  readonly sheetDismissed = output<void>();

  protected readonly status = this.sync.status;
  protected readonly lastSuccessAt = this.sync.lastSuccessAt;
  protected readonly progress = this.sync.progress;
  protected readonly lastError = this.sync.lastError;

  /** Kept for callers that might want to hide the row entirely. We now always
   *  render it so signed-out / free users can discover the feature; the
   *  disabled state shows an upsell-friendly subtitle and routes to the
   *  upsell flow on tap. */
  readonly visible = computed<boolean>(() => true);

  protected readonly title = computed<string>(() => {
    switch (this.status()) {
      case 'disabled': return 'Cloud backup — Pro feature';
      case 'syncing':  return 'Cloud backup';
      case 'offline':  return 'Cloud backup — offline';
      case 'error':    return 'Cloud backup — failed';
      case 'idle':
      default:         return 'Cloud backup';
    }
  });

  protected readonly subtitle = computed<string>(() => {
    switch (this.status()) {
      case 'disabled':
        return ProMessage.CloudBackupGate;
      case 'syncing': {
        const p = this.progress();
        return p.total > 0 ? `Backing up ${p.done} of ${p.total}…` : 'Backing up…';
      }
      case 'offline':
        return 'Offline · connect to back up';
      case 'error':
        return this.lastError() ?? 'Tap to try again';
      case 'idle': {
        const at = this.lastSuccessAt();
        return at ? `Backed up ${formatRelative(at)} · tap to back up` : 'Tap to back up now';
      }
      default:
        return '';
    }
  });

  protected readonly iconName = computed<string>(() => {
    switch (this.status()) {
      case 'disabled':        return 'cloud-outline';
      case 'syncing':         return 'sync-outline';
      case 'offline':         return 'cloud-offline-outline';
      case 'error':           return 'warning-outline';
      case 'idle':
      default:                return 'cloud-done-outline';
    }
  });

  protected readonly iconColor = computed<string>(() => {
    switch (this.status()) {
      case 'error':   return 'danger';
      case 'idle':    return 'success';
      case 'syncing': return 'primary';
      default:        return 'medium';
    }
  });

  protected async openSheet(): Promise<void> {
    // Free / signed-out users see the row purely as a teaser — tapping it
    // opens the Pro paywall rather than a sheet whose toggles do nothing yet.
    if (this.status() === 'disabled') {
      this.proGate.requirePro(ProFeature.CloudSync);
      return;
    }
    // Full-window modal (like Manage Subscriptions), not a half-sheet or centred card.
    const modal = await this.modalCtrl.create({
      component: SyncSheetComponent,
      cssClass: 'sync-fullscreen-modal',
    });
    await modal.present();
    // A backup/restore inside the sheet changes server storage; tell the host to
    // refresh once the sheet closes (the page beneath gets no lifecycle event).
    await modal.onDidDismiss();
    this.sheetDismissed.emit();
  }
}

/** Tiny relative-time formatter — keeps the row free of an extra dependency
 *  on date-fns or Intl.RelativeTimeFormat polyfills. Anything older than a
 *  day falls back to an absolute date so we don't spell out "32 days ago". */
function formatRelative(ts: number): string {
  const diffMs = Date.now() - ts;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return new DatePipe('en').transform(ts, 'mediumDate') ?? '';
}
